import { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { Button } from "@/components/ds/Button"
import Spinner from "@/components/ds/Spinner"
import { Typography } from "@/components/ds/Typography"
import { useFeatureFlagsStatus } from "@/hooks/useFeatureFlags"

import { type ComingSoonRoutesConfig, useComingSoonRoutes } from "./comingSoonRoutes"

/**
 * A path as React Router matches it: segments decoded, trailing slashes dropped and case ignored
 * (routes are not case-sensitive). Every lookup below compares normalised paths on both sides, so
 * `/dashboard/scope-2/energy/` cannot skip a lock or a flag wait that `/dashboard/scope-2/energy` gets.
 */
function normalizePathname(pathname: string): string {
  let decoded = pathname
  try {
    // Same decoding as React Router's matcher: an encoded "/" stays inside its segment.
    decoded = pathname
      .split("/")
      .map((segment) => decodeURIComponent(segment).replace(/\//g, "%2F"))
      .join("/")
  } catch {
    // A malformed escape: the router matches it undecoded too.
  }
  return (decoded.replace(/\/+$/, "") || "/").toLowerCase()
}

/** Expects a normalised `pathname`. */
function matchesPathPrefix(pathname: string, prefix: string): boolean {
  const normalizedPrefix = normalizePathname(prefix)
  return pathname === normalizedPrefix || pathname.startsWith(`${normalizedPrefix}/`)
}

/** The value of the exact entry for a normalised `pathname`, if there is one. */
function exactMatch<T>(pathname: string, exact: Record<string, T>): T | undefined {
  return Object.entries(exact).find(([path]) => normalizePathname(path) === pathname)?.[1]
}

/** The flag that decides this path's screen, if one does (MPE-1367). Expects a normalised `pathname`. */
function gatingFlag(pathname: string, flagGated: ComingSoonRoutesConfig["flagGated"]): string | undefined {
  return (
    exactMatch(pathname, flagGated.exact) ??
    flagGated.prefix.find((gated) => matchesPathPrefix(pathname, gated.prefix))?.flag
  )
}

/**
 * A flag-gated page whose flags have not loaded yet: neither the page nor its
 * Coming Soon screen is known, so show a neutral wait, or a retry when the
 * flags request failed (MPE-1367).
 */
export function FlagsPending({ failed, onRetry }: { failed: boolean; onRetry: () => void }) {
  const { t } = useTranslation()
  if (!failed) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center" role="status" aria-busy="true">
        <Spinner large />
      </div>
    )
  }
  return (
    <div className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-3 p-6 text-center" role="alert">
      <Typography variant="body1" className="text-neutral-title">
        {t("comingSoonRoutes.flagsLoadFailed")}
      </Typography>
      <Button variant="outline" size="small" onClick={onRetry}>
        {t("comingSoonRoutes.retry")}
      </Button>
    </div>
  )
}

const ComingSoonRoute = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  const pathname = normalizePathname(location.pathname)
  const { exact, prefix, flagGated } = useComingSoonRoutes()
  const flag = gatingFlag(pathname, flagGated)
  const flags = useFeatureFlagsStatus(flag)

  if (flag && !flags.ready) {
    return <FlagsPending failed={flags.failed} onRetry={() => void flags.retry()} />
  }

  const ExactComponent = exactMatch(pathname, exact)
  if (ExactComponent) {
    return <ExactComponent />
  }

  for (const { prefix: pathPrefix, Component } of prefix) {
    if (matchesPathPrefix(pathname, pathPrefix)) {
      return <Component />
    }
  }

  return <>{children}</>
}

export default ComingSoonRoute
